import { CandidateSelector } from './CandidateSelector';
import { isDuplicateCandidate } from '../../lib/utils';
import { useCandidateHistory } from './hooks/useCandidateHistory';
import { FileUpload } from './FileUpload';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { User } from 'lucide-react';
import { cn } from "@/lib/utils";

interface CandidateFormProps {
  candidateName: string;
  cvText: string;
  onNameChange: (value: string) => void;
  onCvTextChange: (value: string) => void;
  errors?: {
    name?: string;
    cvText?: string;
  };
  className?: string;
}

export function CandidateForm({
  candidateName,
  cvText,
  onNameChange,
  onCvTextChange,
  errors = {},
  className,
}: CandidateFormProps) {
  const { candidates = [] } = useCandidateHistory();

  const handleNameChange = (value: string) => {
    if (isDuplicateCandidate(value, cvText, candidates)) { return; }
    onNameChange(value);
  };

  const handleCvTextChange = (value: string) => {
    if (isDuplicateCandidate(candidateName, value, candidates)) { return; }
    onCvTextChange(value);
  };

  return (
    <div className={cn("space-y-6 border bg-card text-card-foreground rounded-lg p-6 shadow-sm", className)}>
      <div className="flex items-center justify-between gap-4 pb-4 border-b">
        <div className="flex items-center space-x-3">
          <User className="h-5 w-5 text-primary flex-shrink-0" />
          <h3 className="text-lg font-semibold text-foreground">Candidate Information</h3>
        </div>
        <CandidateSelector
          onSelect={({ name, cvText }) => {
            onNameChange(name);
            onCvTextChange(cvText);
          }}
        />
      </div>

      <div className="space-y-6 pt-2">
        <div className="space-y-1.5">
          <Label htmlFor="candidateName">Candidate Name</Label>
          <Input
            id="candidateName"
            value={candidateName}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Enter candidate's full name"
            required
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "candidateName-error" : undefined}
          />
          {errors.name && (
            <p id="candidateName-error" className="text-xs text-destructive pt-1">
              {errors.name}
            </p>
          )}
        </div>

        <FileUpload
          type="cv"
          onFileSelect={() => {
            // If you need to handle the file itself, do so here.
          }}
          onTextExtracted={(text) => handleCvTextChange(text)}
          onTitleExtracted={(title) => handleNameChange(title)}
          label="Upload CV (PDF or Word Doc)"
        />

        <div className="space-y-1.5">
          <Label htmlFor="cvText">CV Text</Label>
          <Textarea
            id="cvText"
            value={cvText}
            onChange={(e) => handleCvTextChange(e.target.value)}
            placeholder="Paste the candidate's CV content here, or upload a file above..."
            required
            rows={10}
            aria-invalid={!!errors.cvText}
            aria-describedby={errors.cvText ? "cvText-error" : undefined}
          />
          {errors.cvText && (
            <p id="cvText-error" className="text-xs text-destructive pt-1">
              {errors.cvText}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
